import { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Pie } from "react-chartjs-2";
import { isDemoMode as getIsDemoMode } from "../utils/demoAuth";
import { getStoredDemoDashboardData } from "../utils/demoStorage";
import { BASE_URL } from "../utils/apiConfig";
import { buildMonthYearQuery, getMonthName } from "../utils/monthYear";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

function Dashboard({ selectedMonth, selectedYear }) {
  const [demoMode, setDemoMode] = useState(
    () => getIsDemoMode()
  );
  const [expenses, setExpenses] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [prediction, setPrediction] = useState(null);
  const [monthlyComparison, setMonthlyComparison] = useState(null);

  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [dashboardRefreshing, setDashboardRefreshing] = useState(false);

  // ✅ Ask AI states
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [askAiLoading, setAskAiLoading] = useState(false);

  const quickAskAIPrompts = [
    {
      label: "Spending Summary",
      question: "Summarize my spending this month",
    },
    {
      label: "Highest Category",
      question: "What is my highest spending category?",
    },
    {
      label: "Saving Tips",
      question: "Where can I save money this month?",
    },
    {
      label: "Monthly Comparison",
      question: "Compare this month with last month",
    },
    {
      label: "Budget Analysis",
      question: "Analyze my budget usage",
    },
  ];

  const askAICategories = [
    "Grocery",
    "Entertainment",
    "Food",
    "Transport",
    "Bills",
    "Shopping",
    "Rent",
    "Medical",
    "Education",
    "Others",
  ];

  const formatAskAIAnswer = (text) => {
    if (!text) return "";

    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
      .replace(/\. /g, ".<br/>")
      .replace(/\n/g, "<br/>")
      .replace(/₹\s?\d[\d,]*(\.\d+)?/g, "<span class='ai-amount'>$&</span>")
      .replace(
        /\b(Entertainment|Food|Transport|Shopping|Bills|Others)\b/gi,
        "<b class='ai-category'>$1</b>"
      );
  };

  const currencyPattern = /(?:\u20b9|â‚¹)\s?\d[\d,]*(?:\.\d+)?/g;
  const categoryPattern = new RegExp(`\\b(${askAICategories.join("|")})\\b`, "gi");

  const cleanAskAIText = (value) => {
    if (!value) return "";
    const text = String(value);
    formatAskAIAnswer(text);

    return text
      .replace(/\r/g, "\n")
      .replace(/#{1,6}\s*/g, "")
      .replace(/\*\*/g, "")
      .replace(/`/g, "")
      .replace(/^\s*[-*]\s+/gm, "")
      .replace(/\b([A-Za-z]+)(\s+\1\b)+/gi, "$1")
      .replace(/[ \t]+/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  const splitAskAIText = (value) => {
    const cleaned = cleanAskAIText(value);
    if (!cleaned) return [];

    return cleaned
      .split(/\n+|(?<=[.!?])\s+/)
      .map((item) => item.replace(/^[:\s]+/, "").trim())
      .filter(Boolean);
  };

  const getUniqueMatches = (value, pattern) => {
    const matches = cleanAskAIText(value).match(pattern) || [];
    return [...new Set(matches.map((match) => match.trim()))];
  };

  const buildAskAIInsight = (answer) => {
    if (!answer) {
      return {
        state: "empty",
        status: "Ready",
        summary: "Ask AI to analyze your spending patterns.",
        highlights: [],
        recommendation: "",
        indicators: [],
      };
    }

    const rawMessage =
      typeof answer === "string"
        ? answer
        : answer?.message || answer?.detail || answer?.breakdown || "";
    const isError =
      typeof answer === "string" &&
      /failed|error|not reachable|unable|please type/i.test(answer);

    if (isError) {
      return {
        state: "error",
        status: "Needs attention",
        summary: answer.includes("Please type")
          ? "Please enter a spending question to generate an insight."
          : "Unable to generate insight right now.",
        highlights: [],
        recommendation: "Try again in a moment or ask a more specific spending question.",
        indicators: [],
      };
    }

    if (typeof answer !== "string" && answer?.type === "spending_summary") {
      const category = answer.category || "All Categories";
      const categoryLabel =
        category === "All Categories"
          ? category
          : category.charAt(0).toUpperCase() + category.slice(1);
      const amount = `\u20b9${Number(answer.total || 0).toLocaleString("en-IN")}`;
      const transactionCount =
        answer.breakdown && !answer.breakdown.toLowerCase().includes("no expenses")
          ? answer.breakdown.split("+").filter(Boolean).length
          : 0;

      return {
        state: "success",
        status: answer.source === "demo" ? "Demo insight" : "Database insight",
        summary: `${categoryLabel} spending totals ${amount} for the selected month.`,
        highlights: [
          `${categoryLabel}: ${amount}`,
          transactionCount > 0
            ? `${transactionCount} transactions contributed to this total.`
            : "No expense transactions found for this view.",
          category === "All Categories"
            ? "This total covers all spending categories in the selected month."
            : `${categoryLabel} is the selected spending focus.`,
        ],
        recommendation:
          Number(answer.total || 0) > 0
            ? category === "All Categories"
              ? "Review the highest categories first and set monthly limits where spending is concentrated."
              : `Review ${categoryLabel.toLowerCase()} transactions and set a monthly limit if this category is trending high.`
            : "No spending found for this view. Add expenses to unlock sharper recommendations.",
        indicators: [
          { label: "Category", value: categoryLabel },
          { label: "Total", value: amount },
          { label: "Source", value: answer.source || "database" },
        ],
      };
    }

    const lines = splitAskAIText(rawMessage);
    const categories = getUniqueMatches(rawMessage, categoryPattern);
    const amounts = getUniqueMatches(rawMessage, currencyPattern);
    const recommendationLines = lines.filter((line) =>
      /recommend|consider|save|saving|reduce|limit|budget|avoid|should|try/i.test(line)
    );
    const highlightLines = lines
      .filter((line) => {
        currencyPattern.lastIndex = 0;
        categoryPattern.lastIndex = 0;
        return (
          currencyPattern.test(line) ||
          categoryPattern.test(line) ||
          /highest|summary|total|trend|month|spending/i.test(line)
        );
      })
      .slice(0, 4);

    return {
      state: "success",
      status: typeof answer !== "string" && answer?.source === "gemini" ? "Gemini insight" : "AI insight",
      summary:
        lines.find((line) => !recommendationLines.includes(line)) ||
        "Financial insight generated from your selected month.",
      highlights:
        highlightLines.length > 0
          ? highlightLines
          : lines.filter((line) => !recommendationLines.includes(line)).slice(0, 3),
      recommendation:
        recommendationLines[0] ||
        "Keep reviewing high-frequency categories and set limits where spending feels repetitive.",
      indicators: [
        { label: "Amounts", value: amounts.length > 0 ? amounts.slice(0, 2).join(" / ") : "Not specified" },
        { label: "Categories", value: categories.length > 0 ? categories.slice(0, 2).join(", ") : "All spending" },
        { label: "Focus", value: recommendationLines.length > 0 ? "Savings action" : "Spending review" },
      ],
    };
  };

  const renderInsightText = (value) => {
    const parts = cleanAskAIText(value).split(/((?:\u20b9|â‚¹)\s?\d[\d,]*(?:\.\d+)?|\b(?:Grocery|Entertainment|Food|Transport|Bills|Shopping|Rent|Medical|Education|Others)\b)/gi);

    return parts.map((part, index) => {
      if (!part) return null;
      if (/^(?:\u20b9|â‚¹)\s?\d/i.test(part)) {
        return <span key={`${part}-${index}`} className="ai-amount">{part}</span>;
      }
      categoryPattern.lastIndex = 0;
      if (categoryPattern.test(part)) {
        categoryPattern.lastIndex = 0;
        return <span key={`${part}-${index}`} className="ai-category">{part}</span>;
      }
      categoryPattern.lastIndex = 0;
      return part;
    });
  };

  const normalizeAskAIQuestion = (question) => {
    const trimmedQuestion = question.trim();

    if (/\b(travel|travels|travelling|traveling|trip|journey|commute|commuting)\b/i.test(trimmedQuestion)) {
      return trimmedQuestion.replace(
        /\b(travel|travels|travelling|traveling|trip|journey|commute|commuting)\b/gi,
        "transport"
      );
    }

    return trimmedQuestion;
  };

  const getAskAICards = (text) => {

    if (typeof text !== "string") {
  if (text?.type === "spending_summary") {
    return [
      {
  label: "Category",
  title: text.category || "All Categories",
  amount: `₹${Number(text.total || 0).toLocaleString("en-IN")}`,
  detail: text.breakdown,
  source: text.source || "database",
},
    ];
  }

  return [
    {
      title: "AI Response",
      amount: "",
      detail: text?.message || "No response",
      source: text?.source || "AI",
    },
  ];
}

    if (!text) return [];

    const amountMatches = [...text.matchAll(/₹\s?\d[\d,]*(\.\d+)?/g)].map(
      (match) => ({
        amount: match[0],
        index: match.index || 0,
      })
    );

    const foundCategories = askAICategories
      .map((category) => {
        const match = text.match(new RegExp(`\\b${category}\\b`, "i"));
        return match
          ? {
              category,
              index: match.index || 0,
            }
          : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.index - b.index)
      .slice(0, 3);

    if (foundCategories.length === 0 && amountMatches.length === 0) {
      return [];
    }

    const sentenceParts = text
      .split(/(?<=[.!?])\s+/)
      .map((sentence) => sentence.trim())
      .filter(Boolean);

    const categoriesForCards =
      foundCategories.length > 0
        ? foundCategories
        : [{ category: "Spending", index: amountMatches[0]?.index || 0 }];

    return categoriesForCards.map((item, index) => {
      const nearestAmount =
        amountMatches
          .slice()
          .sort(
            (a, b) =>
              Math.abs(a.index - item.index) - Math.abs(b.index - item.index)
          )[0]?.amount ||
        amountMatches[index]?.amount ||
        "Amount not mentioned";

      const suggestion =
        sentenceParts.find((sentence) =>
          sentence.toLowerCase().includes(item.category.toLowerCase())
        ) ||
        sentenceParts.find((sentence) => sentence.includes(nearestAmount)) ||
        text;

      return {
        category: item.category,
        amount: nearestAmount,
        suggestion,
      };
    });
  };

  const handleAskAI = async () => {
    if (!aiQuestion || !aiQuestion.trim()) {
      setAiAnswer("Please type a question first.");
      return;
    }

    if (demoMode) {
      const demoData = getStoredDemoDashboardData(selectedMonth, selectedYear);
      setAiAnswer({
        type: "spending_summary",
        category: "Demo Data",
        total: demoData.aiInsights.totalSpend,
        breakdown: "Sample dashboard total based on frontend-only demo expenses.",
        source: "demo",
      });
      return;
    }

    try {
      setAskAiLoading(true);
      setAiAnswer("");

      const token = localStorage.getItem("token");

      const res = await fetch(`${BASE_URL}/api/ask-ai`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          question: normalizeAskAIQuestion(aiQuestion),
          month: selectedMonth,
          year: selectedYear,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setAiAnswer(data.error || "Ask AI failed.");
        return;
      }

      setAiAnswer(data.answer || "");

    } catch {
      setAiAnswer("Backend not reachable. Please check node server.js.");
    } finally {
      setAskAiLoading(false);
    }
  };

  const resetAskAI = () => {
    setAiQuestion("");
    setAiAnswer("");
  };

  useEffect(() => {
    setDemoMode(getIsDemoMode());
    loadDashboardData();
    loadAIInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, demoMode]);

  const format = (num) => new Intl.NumberFormat("en-IN").format(num || 0);
  const formatMoney = (value) => Number(value || 0).toLocaleString("en-IN");

  const loadDashboardData = async () => {
    if (demoMode) {
      const demoData = getStoredDemoDashboardData(selectedMonth, selectedYear);
      setExpenses(demoData.expenses);
      setIncomes(demoData.incomes);
      setBudgets(demoData.budgets);
      setPrediction(demoData.prediction);
      setMonthlyComparison(demoData.monthlyComparison);
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const query = buildMonthYearQuery(selectedMonth, selectedYear);
      const fetchJson = async (url) => {
        const response = await fetch(url, { headers });
        const data = await response.json();

        if (!response.ok || data?.error) {
          throw new Error(data?.error || "Dashboard request failed");
        }

        return data;
      };

      const [
        expenseResult,
        incomeResult,
        budgetResult,
        predictionResult,
        comparisonResult,
      ] = await Promise.allSettled([
        fetchJson(`${BASE_URL}/api/expenses?${query}`),
        fetchJson(`${BASE_URL}/api/income?${query}`),
        fetchJson(`${BASE_URL}/api/budgets?${query}`),
        fetchJson(`${BASE_URL}/api/predictions/monthly?${query}`),
        fetchJson(`${BASE_URL}/api/monthly-comparison?${query}`),
      ]);

      const expenseData =
        expenseResult.status === "fulfilled" && Array.isArray(expenseResult.value)
          ? expenseResult.value
          : [];
      const incomeData =
        incomeResult.status === "fulfilled" && Array.isArray(incomeResult.value)
          ? incomeResult.value
          : [];
      const budgetData =
        budgetResult.status === "fulfilled" && Array.isArray(budgetResult.value)
          ? budgetResult.value
          : [];
      const predictionData =
        predictionResult.status === "fulfilled" ? predictionResult.value : null;
      const comparisonData =
        comparisonResult.status === "fulfilled" ? comparisonResult.value : null;

      setExpenses(expenseData);
      setIncomes(incomeData);
      setBudgets(budgetData);
      setPrediction(predictionData && !predictionData.error ? predictionData : null);
      setMonthlyComparison(
        comparisonData && !comparisonData.error ? comparisonData : null
      );
    } catch {
      setExpenses([]);
      setIncomes([]);
      setBudgets([]);
      setPrediction(null);
      setMonthlyComparison(null);
    }
  };

  const loadAIInsights = async () => {
    if (demoMode) {
      const demoData = getStoredDemoDashboardData(selectedMonth, selectedYear);
      setAiInsights(demoData.aiInsights);
      setAiLoading(false);
      return;
    }

    try {
      setAiLoading(true);

      const token = localStorage.getItem("token");

      const query = buildMonthYearQuery(selectedMonth, selectedYear);
      const res = await fetch(`${BASE_URL}/api/ai-insights?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      setAiInsights(data && !data.error ? data : null);
    } catch {
      setAiInsights(null);
    } finally {
      setAiLoading(false);
    }
  };

  const handleRefreshDashboard = async () => {
    try {
      setDashboardRefreshing(true);
      setAiQuestion("");
      setAiAnswer("");
      await Promise.all([loadDashboardData(), loadAIInsights()]);
    } finally {
      setDashboardRefreshing(false);
    }
  };

  // ===== TOTALS =====
  const totalExpense = expenses.reduce(
    (sum, e) => sum + Number(e.amount || 0),
    0
  );

  const totalIncome = incomes.reduce(
    (sum, i) => sum + Number(i.amount || 0),
    0
  );

  const balance = totalIncome - totalExpense;

  // ===== CATEGORY TOTALS =====
  const expenseCategoryTotals = expenses.reduce((acc, e) => {
    const category = (e.category || "others").toLowerCase();
    acc[category] = (acc[category] || 0) + Number(e.amount || 0);
    return acc;
  }, {});
  const aiCategoryTotals = aiInsights?.categoryBreakdown || {};
  const categoryTotals =
    Object.keys(expenseCategoryTotals).length > 0
      ? expenseCategoryTotals
      : aiCategoryTotals;
  const categoryTotalExpense = Object.values(categoryTotals).reduce(
    (sum, amount) => sum + Number(amount || 0),
    0
  );

  const topCategories = Object.entries(categoryTotals)
    .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))
    .slice(0, 3);

  const topCategoryStats = topCategories.map(([category, amount]) => ({
    category,
    amount: Number(amount || 0),
    percentage:
      categoryTotalExpense > 0
        ? Math.round((Number(amount || 0) / categoryTotalExpense) * 100)
        : 0,
  }));

  const topCategory = topCategories[0];

  let smartInsight =
    "No expenses added yet. Start adding expenses to see insights.";

  if (expenses.length > 0 && topCategory && totalExpense > 0) {
    const percent = ((topCategory[1] / totalExpense) * 100).toFixed(1);
    smartInsight = `You spend most on ${topCategory[0]} (${percent}%) — ₹${format(
      topCategory[1]
    )}.`;
  }

  // ===== MONTH COMPARISON =====
  const fallbackPreviousDate = new Date(Number(selectedYear), Number(selectedMonth) - 2, 1);
  const fallbackPreviousMonth = fallbackPreviousDate.getMonth() + 1;
  const fallbackPreviousYear = fallbackPreviousDate.getFullYear();
  const comparison = monthlyComparison || {
    selectedMonth,
    selectedYear,
    previousMonth: fallbackPreviousMonth,
    previousYear: fallbackPreviousYear,
    currentTotal: totalExpense,
    previousTotal: 0,
    difference: totalExpense,
    percentageChange: totalExpense > 0 ? 100 : 0,
    trend: totalExpense > 0 ? "increased" : "same",
    message:
      "Monthly comparison is unavailable. Restart the backend if this persists.",
  };
  const comparisonTrend = comparison.trend || "same";
  let monthComparisonStatus =
    comparisonTrend === "increased"
      ? "danger"
      : comparisonTrend === "decreased"
      ? "success"
      : "neutral";
  const comparisonIcon =
    comparisonTrend === "increased"
      ? "🔺"
      : comparisonTrend === "decreased"
      ? "🔻"
      : "➖";
  const comparisonDirection =
    comparisonTrend === "increased"
      ? "higher"
      : comparisonTrend === "decreased"
      ? "lower"
      : "same as";
  const comparisonDifference = Number(comparison.difference || 0);
  const comparisonPercentage = Number(comparison.percentageChange || 0);
  const comparisonDifferencePrefix =
    comparisonDifference > 0 ? "+" : comparisonDifference < 0 ? "-" : "";
  const comparisonPreviousMonth =
    comparison.previousMonth || fallbackPreviousDate.getMonth() + 1;
  const comparisonPreviousYear =
    comparison.previousYear || fallbackPreviousDate.getFullYear();
  const comparisonPreviousTotal = Number(comparison.previousTotal || 0);
  const isLowPreviousBase =
    comparisonTrend === "increased" && comparisonPreviousTotal > 0 && comparisonPreviousTotal < 2000;

  const comparisonPercentageText = isLowPreviousBase
    ? "Spending increased significantly compared to last month"
    : `${comparisonDifferencePrefix}${Math.abs(comparisonPercentage).toFixed(1)}% ${comparisonDirection} previous month`;

  let monthComparisonText =
    comparison.message ||
    `${comparisonDifferencePrefix}${Math.abs(comparisonPercentage).toFixed(1)}% ${comparisonDirection} previous month`;

  if (isLowPreviousBase) {
    monthComparisonText = `Your spending increased significantly compared to ${getMonthName(
      comparisonPreviousMonth
    )} ${comparisonPreviousYear}.`;
  }
  // ===== BUDGET ALERTS =====
  const budgetAlerts = budgets.map((budget) => {
    const category = budget.category.toLowerCase();
    const spent = categoryTotals[category] || 0;
    const budgetAmount = Number(budget.amount || 0);
    const percentage = budgetAmount > 0 ? (spent / budgetAmount) * 100 : 0;
    const difference = spent - budgetAmount;

    

    return {
      category,
      spent,
      budgetAmount,
      percentage,
      difference,
      status:
        spent > budgetAmount
          ? "exceeded"
          : percentage >= 80
          ? "warning"
          : "safe",
    };
  });

  // ===== FUTURE SPENDING TREND =====
  let futureBudgetRisk = null;

  if (prediction && prediction.predictedTotal > 0) {
    const currentSpend = totalExpense || 0;
    const predicted = prediction.predictedTotal;

    if (currentSpend > 0) {
      const difference = predicted - currentSpend;
      const percent = ((difference / currentSpend) * 100).toFixed(1);

      if (difference > 0) {
        futureBudgetRisk = {
          status: "danger",
          message: `Your spending may increase by ${percent}% next month (₹${format(
            difference
          )} more than this month).`,
        };
      } else {
        futureBudgetRisk = {
          status: "success",
          message: `Good trend! You may reduce spending by ${Math.abs(
            percent
          )}% next month (₹${format(Math.abs(difference))} less).`,
        };
      }
    }
  }

  // ===== CATEGORY BUDGET PREDICTION =====
  let categoryBudgetRisks = [];

  if (prediction?.categoryPrediction && budgets.length > 0) {
    Object.entries(prediction.categoryPrediction).forEach(
      ([category, predictedAmount]) => {
        const budget = budgets.find(
          (b) => b.category.toLowerCase() === category.toLowerCase()
        );

        if (budget) {
          const budgetAmount = Number(budget.amount || 0);

          if (budgetAmount > 0 && predictedAmount > budgetAmount) {
            const diff = predictedAmount - budgetAmount;
            const percent = ((diff / budgetAmount) * 100).toFixed(1);

            categoryBudgetRisks.push({
              type: "danger",
              title: `⚠️ ${category} budget risk`,
              message: `${category} may exceed budget by ${percent}% (₹${format(
                diff
              )}) next month.`,
            });
          }
        }
      }
    );
  }

  // ===== AI ALERTS =====
  const aiAlerts = [];

  if (categoryBudgetRisks.length > 0) {
    categoryBudgetRisks.forEach((alert) => aiAlerts.push(alert));
  }

  if (futureBudgetRisk) {
    aiAlerts.push({
      type: futureBudgetRisk.status,
      title: "Future Spending Trend",
      message: futureBudgetRisk.message,
    });
  }

  if (totalIncome === 0 && totalExpense > 0) {
    aiAlerts.push({
      type: "warning",
      title: "Income missing",
      message: "Expenses are added, but income is not recorded yet.",
    });
  }

  if (totalIncome > 0 && balance < totalIncome * 0.2) {
    aiAlerts.push({
      type: "danger",
      title: "Low balance warning",
      message: "Your remaining balance is below 20% of total income.",
    });
  }

  if (topCategory && totalExpense > 0) {
    const topPercent = (topCategory[1] / totalExpense) * 100;

    if (topPercent >= 50) {
      aiAlerts.push({
        type: "info",
        title: "High category dependency",
        message: `${topCategory[0]} takes ${topPercent.toFixed(
          0
        )}% of your total spending.`,
      });
    }
  }

  const exceededBudgetCount = budgetAlerts.filter(
    (alert) => alert.status === "exceeded"
  ).length;
  const visibleBudgetAlerts =
    exceededBudgetCount > 0
      ? budgetAlerts.filter((alert) => alert.status === "exceeded")
      : budgetAlerts
          .slice()
          .sort((a, b) => Number(b.percentage || 0) - Number(a.percentage || 0))
          .slice(0, 5);

  if (exceededBudgetCount > 0) {
    aiAlerts.push({
      type: "danger",
      title: "Budget risk detected",
      message: `${exceededBudgetCount} budget category exceeded the limit.`,
    });
  }

  // ===== AI FINANCIAL HEALTH SCORE =====
  const clampScore = (value, min = 0, max = 100) =>
    Math.min(max, Math.max(min, Number.isFinite(value) ? value : 0));

  const savingsRate = totalIncome > 0 ? (balance / totalIncome) * 100 : 0;
  const expenseIncomeRatio =
    totalIncome > 0 ? (totalExpense / totalIncome) * 100 : totalExpense > 0 ? 100 : 0;
  const totalBudgetAmount = budgets.reduce(
    (sum, budget) => sum + Number(budget.amount || 0),
    0
  );
  const budgetUsage =
    totalBudgetAmount > 0 ? (totalExpense / totalBudgetAmount) * 100 : null;
  const highestCategoryName = topCategory?.[0] || "No spending yet";
  const highestCategoryShare =
    topCategory && totalExpense > 0
      ? (Number(topCategory[1] || 0) / totalExpense) * 100
      : 0;
  const lowTrendBaseLimit = 1000;
  const reasonableTrendPercentLimit = 150;
  const signedTrendDifference =
    comparisonTrend === "decreased"
      ? -Math.abs(comparisonDifference)
      : comparisonTrend === "increased"
      ? Math.abs(comparisonDifference)
      : 0;
  const rawSpendingTrendPercent =
    comparisonPreviousTotal > 0
      ? (signedTrendDifference / comparisonPreviousTotal) * 100
      : totalExpense > 0
      ? 100
      : 0;
  const absoluteTrendPercent = Math.abs(rawSpendingTrendPercent);
  const hasLowTrendBase =
    comparisonPreviousTotal > 0 && comparisonPreviousTotal < lowTrendBaseLimit;
  const canShowTrendPercent =
    comparisonPreviousTotal >= lowTrendBaseLimit &&
    absoluteTrendPercent <= reasonableTrendPercentLimit;
  const getSpendingTrendInsight = () => {
    if (comparisonPreviousTotal <= 0) {
      return {
        label: totalExpense > 0 ? "New spending activity" : "Spending stable",
        scorePercent: totalExpense > 0 ? 22 : 0,
      };
    }

    if (hasLowTrendBase && signedTrendDifference > 0) {
      return {
        label: "Spending increased significantly",
        scorePercent: 35,
      };
    }

    if (hasLowTrendBase && signedTrendDifference < 0) {
      return {
        label: "Large decrease from last month",
        scorePercent: -35,
      };
    }

    if (absoluteTrendPercent <= 5) {
      return {
        label: "Spending stable",
        scorePercent: 0,
      };
    }

    if (rawSpendingTrendPercent > 0) {
      if (canShowTrendPercent && absoluteTrendPercent <= 25) {
        return {
          label: `${Math.round(absoluteTrendPercent)}% higher`,
          scorePercent: rawSpendingTrendPercent,
        };
      }

      return {
        label:
          absoluteTrendPercent <= 45
            ? "Moderate increase from last month"
            : "Spending increased significantly",
        scorePercent: Math.min(rawSpendingTrendPercent, 45),
      };
    }

    if (canShowTrendPercent && absoluteTrendPercent <= 15) {
      return {
        label: `${Math.round(absoluteTrendPercent)}% lower`,
        scorePercent: rawSpendingTrendPercent,
      };
    }

    return {
      label:
        absoluteTrendPercent <= 35
          ? "Slight decrease from last month"
          : "Large decrease from last month",
      scorePercent: Math.max(rawSpendingTrendPercent, -45),
    };
  };
  const spendingTrendInsight = getSpendingTrendInsight();
  const spendingTrendPercent = spendingTrendInsight.scorePercent;

  const savingsComponent =
    totalIncome > 0
      ? clampScore(savingsRate <= 0 ? savingsRate * 2 : 45 + savingsRate * 1.4)
      : totalExpense > 0
      ? 18
      : 50;
  const expenseRatioComponent =
    totalIncome > 0
      ? clampScore(100 - Math.max(0, expenseIncomeRatio - 45) * 1.45)
      : totalExpense > 0
      ? 20
      : 55;
  const budgetComponent =
    budgetUsage === null
      ? 58
      : clampScore(100 - Math.max(0, budgetUsage - 70) * 1.6 - exceededBudgetCount * 12);
  const overBudgetComponent = clampScore(100 - exceededBudgetCount * 24);
  const trendComponent =
    comparisonPreviousTotal > 0
      ? clampScore(76 - Math.max(0, spendingTrendPercent) * 0.9 + Math.max(0, -spendingTrendPercent) * 0.35)
      : 62;
  const concentrationComponent = clampScore(
    100 - Math.max(0, highestCategoryShare - 35) * 1.25
  );

  const financialHealthScore = Math.round(
    clampScore(
      savingsComponent * 0.24 +
        expenseRatioComponent * 0.22 +
        budgetComponent * 0.2 +
        overBudgetComponent * 0.12 +
        trendComponent * 0.12 +
        concentrationComponent * 0.1
    )
  );
  const healthStatus =
    financialHealthScore >= 80
      ? "Excellent"
      : financialHealthScore >= 65
      ? "Good"
      : financialHealthScore >= 45
      ? "Moderate"
      : "Needs Attention";
  const healthStatusClass =
    financialHealthScore >= 80
      ? "excellent"
      : financialHealthScore >= 65
      ? "good"
      : financialHealthScore >= 45
      ? "moderate"
      : "needs-attention";
  const healthScoreStyle = {
    "--health-score": financialHealthScore,
    "--health-color":
      healthStatusClass === "excellent"
        ? "#22c55e"
        : healthStatusClass === "good"
        ? "#38bdf8"
        : healthStatusClass === "moderate"
        ? "#f59e0b"
        : "#fb7185",
  };
  const budgetRiskLabel =
    budgets.length === 0
      ? "Budget data limited"
      : exceededBudgetCount > 0
      ? `${exceededBudgetCount} over budget`
      : budgetUsage !== null
      ? `${Math.round(budgetUsage)}% used`
      : "Budget data limited";
  const spendingTrendLabel = spendingTrendInsight.label;
  const healthSignals = [
    {
      label: "Savings Rate",
      value: totalIncome > 0 ? `${Math.max(0, Math.round(savingsRate))}%` : "Income needed",
    },
    {
      label: "Budget Risk",
      value: budgetRiskLabel,
    },
    {
      label: "Spending Trend",
      value: spendingTrendLabel,
    },
    {
      label: "Highest Category",
      value:
        topCategory && totalExpense > 0
          ? `${highestCategoryName} (${Math.round(highestCategoryShare)}%)`
      : highestCategoryName,
    },
  ];

  // ===== SMART AI RECOMMENDATIONS =====
  const recommendationCandidates = [];
  const addRecommendation = (recommendation) => {
    if (
      recommendation &&
      recommendation.title &&
      !recommendationCandidates.some((item) => item.title === recommendation.title)
    ) {
      recommendationCandidates.push(recommendation);
    }
  };

  if (topCategory && totalExpense > 0 && highestCategoryShare >= 45) {
    addRecommendation({
      type: highestCategoryShare >= 55 ? "critical" : "warning",
      priority: highestCategoryShare >= 55 ? "High" : "Medium",
      rank: highestCategoryShare >= 55 ? 98 : 82,
      title: `${highestCategoryName} concentration risk`,
      message: `${highestCategoryName} contributes ${Math.round(
        highestCategoryShare
      )}% of this month's spending. Review recurring items in this category first.`,
    });
  }

  const highestBudgetAlert = budgetAlerts
    .slice()
    .sort((a, b) => Number(b.percentage || 0) - Number(a.percentage || 0))[0];

  if (exceededBudgetCount > 0) {
    addRecommendation({
      type: "critical",
      priority: "High",
      rank: 96,
      title: "Budget limits need attention",
      message:
        exceededBudgetCount === 1
          ? `${highestBudgetAlert?.category || "One category"} exceeded its monthly budget.`
          : `${exceededBudgetCount} categories exceeded their monthly budget.`,
    });
  } else if (highestBudgetAlert && highestBudgetAlert.percentage >= 80) {
    addRecommendation({
      type: "warning",
      priority: "Medium",
      rank: 78,
      title: `${highestBudgetAlert.category} is close to budget`,
      message: `${highestBudgetAlert.category} has used ${Math.round(
        highestBudgetAlert.percentage
      )}% of its monthly budget.`,
    });
  } else if (budgets.length > 0 && totalExpense > 0) {
    addRecommendation({
      type: "positive",
      priority: "Low",
      rank: 48,
      title: "Budgets are under control",
      message: "No category has crossed its monthly budget based on current spending.",
    });
  }

  if (totalIncome > 0 && savingsRate >= 20) {
    addRecommendation({
      type: "positive",
      priority: "Low",
      rank: 72,
      title: "Savings rate looks healthy",
      message: `You retained ${Math.round(
        savingsRate
      )}% of income this month. Keep this cushion protected.`,
    });
  } else if (totalIncome > 0 && savingsRate < 10) {
    addRecommendation({
      type: savingsRate < 0 ? "critical" : "warning",
      priority: savingsRate < 0 ? "High" : "Medium",
      rank: savingsRate < 0 ? 94 : 84,
      title: savingsRate < 0 ? "Expenses are above income" : "Savings buffer is thin",
      message:
        savingsRate < 0
          ? "This month's expenses are higher than income. Prioritize controllable categories."
          : "Your savings rate is below 10%. A small category reset could improve cash flow.",
    });
  } else if (totalIncome === 0 && totalExpense > 0) {
    addRecommendation({
      type: "info",
      priority: "Medium",
      rank: 70,
      title: "Income data is missing",
      message: "Add income entries to unlock sharper savings and affordability recommendations.",
    });
  }

  if (topCategory && totalExpense > 0 && highestCategoryShare >= 30) {
    const potentialSavings = Math.round(Number(topCategory[1] || 0) * 0.12);

    if (potentialSavings > 0) {
      addRecommendation({
        type: "info",
        priority: highestCategoryShare >= 45 ? "Medium" : "Low",
        rank: highestCategoryShare >= 45 ? 74 : 52,
        title: "Savings opportunity detected",
        message: `Reducing ${highestCategoryName} by 12% may save about \u20b9${format(
          potentialSavings
        )} this month.`,
      });
    }
  }

  if (signedTrendDifference > 0 && totalExpense > 0) {
    addRecommendation({
      type:
        spendingTrendLabel === "Spending increased significantly"
          ? "warning"
          : "info",
      priority:
        spendingTrendLabel === "Spending increased significantly"
          ? "Medium"
          : "Low",
      rank:
        spendingTrendLabel === "Spending increased significantly" ? 76 : 58,
      title: "Spending trend moved up",
      message: `${spendingTrendLabel}. Compare recent transactions with last month before adding new commitments.`,
    });
  } else if (signedTrendDifference < 0 && comparisonPreviousTotal > 0) {
    addRecommendation({
      type: "positive",
      priority: "Low",
      rank: 56,
      title: "Spending trend improved",
      message: `${spendingTrendLabel}. Keep the same discipline in high-frequency categories.`,
    });
  }

  if (financialHealthScore < 45) {
    addRecommendation({
      type: "critical",
      priority: "High",
      rank: 100,
      title: "Health score needs action",
      message: "Focus on budget breaches, high concentration categories, and income coverage first.",
    });
  } else if (financialHealthScore >= 80) {
    addRecommendation({
      type: "positive",
      priority: "Low",
      rank: 66,
      title: "Strong financial momentum",
      message: "Your current month behavior shows a healthy balance between spending and savings.",
    });
  }

  if (recommendationCandidates.length === 0) {
    addRecommendation({
      type: "info",
      priority: "Low",
      rank: 40,
      title: "Add more financial activity",
      message: "Record income, expenses, and budgets to unlock proactive recommendations.",
    });
  }

  const smartRecommendations = recommendationCandidates
    .sort((a, b) => Number(b.rank || 0) - Number(a.rank || 0))
    .slice(0, 4);

  // ===== CHART DATA =====
  const monthlyTotals = {};

  expenses.forEach((expense) => {
    const date = new Date(expense.createdAt);
    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;

    monthlyTotals[monthKey] =
      (monthlyTotals[monthKey] || 0) + Number(expense.amount || 0);
  });

  const sortedMonths = Object.keys(monthlyTotals).sort();

  const labels = sortedMonths.map((key) => {
    const [year, month] = key.split("-");
    return new Date(year, month).toLocaleString("default", {
      month: "short",
    });
  });

  const values = sortedMonths.map((key) => monthlyTotals[key]);

  if (prediction && prediction.predictedTotal) {
    labels.push("Next");
    values.push(prediction.predictedTotal);
  }

  const lineData = {
    labels,
    datasets: [
      {
        label: "Monthly Expenses + Prediction",
        data: values,
        tension: 0.45,
        fill: true,
        borderColor: "#38bdf8",
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return "rgba(56, 189, 248, 0.18)";
          }

          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, "rgba(56, 189, 248, 0.32)");
          gradient.addColorStop(0.55, "rgba(139, 92, 246, 0.14)");
          gradient.addColorStop(1, "rgba(15, 23, 42, 0.02)");
          return gradient;
        },
        borderWidth: 4,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: "#0f172a",
        pointBorderColor: "#7dd3fc",
        pointBorderWidth: 3,
        pointHoverBackgroundColor: "#38bdf8",
        pointHoverBorderColor: "#ffffff",
        pointHitRadius: 14,
        borderDash: prediction ? [5, 5] : [],
      },
    ],
  };

  const pieData = {
    labels: Object.keys(categoryTotals),
    datasets: [
      {
        label: "Category Spending",
        data: Object.values(categoryTotals),
        backgroundColor: [
          "#38bdf8",
          "#22c55e",
          "#a78bfa",
          "#f59e0b",
          "#fb7185",
          "#14b8a6",
          "#f472b6",
        ],
        borderColor: "#0f172a",
        borderWidth: 4,
        hoverOffset: 14,
        spacing: 3,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index",
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(15, 23, 42, 0.96)",
        titleColor: "#e0f2fe",
        bodyColor: "#f8fafc",
        borderColor: "rgba(125, 211, 252, 0.28)",
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        titleFont: {
          size: 13,
          weight: "700",
        },
        bodyFont: {
          size: 13,
          weight: "700",
        },
        callbacks: {
          label: (context) => `₹${format(context.raw)}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "#cbd5e1",
          font: {
            size: 12,
            weight: "700",
          },
          padding: 10,
        },
      },
      y: {
        beginAtZero: true,
        border: {
          display: false,
        },
        grid: {
          color: "rgba(148, 163, 184, 0.14)",
          drawTicks: false,
        },
        ticks: {
          color: "#cbd5e1",
          font: {
            size: 12,
            weight: "700",
          },
          padding: 10,
          callback: (value) => `₹${format(value)}`,
        },
      },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "62%",
    radius: "92%",
    plugins: {
      legend: {
        position: "bottom",
        align: "center",
        labels: {
          color: "#dbeafe",
          boxWidth: 10,
          boxHeight: 10,
          usePointStyle: true,
          pointStyle: "circle",
          padding: 16,
          font: {
            size: 12,
            weight: "700",
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(15, 23, 42, 0.96)",
        titleColor: "#e0f2fe",
        bodyColor: "#f8fafc",
        borderColor: "rgba(125, 211, 252, 0.28)",
        borderWidth: 1,
        padding: 12,
        bodyFont: {
          size: 13,
          weight: "700",
        },
        callbacks: {
          label: (context) => `${context.label}: ₹${format(context.raw)}`,
        },
      },
    },
  };

  const askAISmartCards = getAskAICards(aiAnswer);
  const askAIInsight = buildAskAIInsight(aiAnswer);
  const aiCategoryBreakdown = aiInsights?.categoryBreakdown || {};
  const aiCategoryEntries = Object.entries(aiCategoryBreakdown);
  const aiTopCategoryEntry = aiCategoryEntries.sort(
    (a, b) => Number(b[1] || 0) - Number(a[1] || 0)
  )[0];
  const aiTotalSpend = aiInsights?.totalSpend || 0;
  const aiTopCategory = aiInsights?.topCategory || aiTopCategoryEntry?.[0] || "-";
  const aiTopAmount = aiInsights?.topAmount || aiTopCategoryEntry?.[1] || 0;
  const aiExpenseCount = aiInsights?.expenseCount || 0;
  const aiInsightText = "Review your highest categories regularly.";

  return (
    <div className="page dashboard-modern dashboard-premium-page">
      <div className="dashboard-topbar">
        <div>
          <div className="dashboard-title-badges">
            <span className="soft-badge">AI Finance Command Center</span>
            {demoMode && <span className="demo-mode-badge">Demo Mode</span>}
          </div>
          <h2>
            {getMonthName(selectedMonth)} {selectedYear} Dashboard
          </h2>
          <p>
            {demoMode
              ? "Showing sample data. Your real expenses are hidden."
              : "Track balance, spending patterns, predictions, and budget risk in one view."}
          </p>
        </div>

        <div className="dashboard-topbar-actions">
          <button
            type="button"
            className="glow-button"
            onClick={handleRefreshDashboard}
            disabled={dashboardRefreshing}
          >
            {dashboardRefreshing ? "Refreshing..." : "Refresh"}
          </button>
          <span className="soft-badge">Gemini Ready</span>
        </div>
      </div>

      <div className="dashboard-summary-grid">
        <div className="premium-stat-card balance-card">
          <span>Total Balance</span>
          <strong style={{ color: balance >= 0 ? "#7dd3fc" : "#fb7185" }}>
            {"\u20b9"}{format(balance)}
          </strong>
          <p>{balance >= 0 ? "Available after expenses" : "Expenses exceed income"}</p>
        </div>

        <div className="premium-stat-card expense-card">
          <span>Monthly Expenses</span>
          <strong>{"\u20b9"}{format(totalExpense)}</strong>
          <p>{expenses.length} transactions recorded</p>
        </div>

        <div className="premium-stat-card income-card">
          <span>Monthly Income</span>
          <strong>{"\u20b9"}{format(totalIncome)}</strong>
          <p>{incomes.length} income entries</p>
        </div>

        <div className="premium-stat-card savings-card">
          <span>Net Savings</span>
          <strong>{"\u20b9"}{format(balance)}</strong>
          <p>{totalIncome > 0 ? `${Math.max(0, Math.round((balance / totalIncome) * 100))}% of income remaining` : "Add income to track savings"}</p>
        </div>
      </div>

      <div
        className={`premium-panel financial-health-card ${healthStatusClass}`}
        style={healthScoreStyle}
      >
        <div className="financial-health-score-wrap">
          <div className="health-score-ring" aria-label={`Financial health score ${financialHealthScore} out of 100`}>
            <div className="health-score-inner">
              <strong>{financialHealthScore}</strong>
              <span>/100</span>
            </div>
          </div>

          <div className="financial-health-copy">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">AI Financial Health Score</span>
                <h3>Financial Health Score</h3>
              </div>
              <span className={`health-status-badge ${healthStatusClass}`}>
                {healthStatus}
              </span>
            </div>

            <p>
              Based on your income, spending, savings, and budget usage.
            </p>
          </div>
        </div>

        <div className="health-signal-grid">
          {healthSignals.map((signal) => (
            <div key={signal.label} className="health-signal-chip">
              <span>{signal.label}</span>
              <strong>{signal.value}</strong>
            </div>
          ))}
        </div>
      </div>

      <div className="premium-panel smart-recommendations-panel">
        <div className="panel-title-row">
          <div>
            <span className="soft-badge">Smart AI Recommendations</span>
            <h3>Advisor Recommendations</h3>
          </div>
          <p>{smartRecommendations.length} insights</p>
        </div>

        <div className="smart-recommendation-grid">
          {smartRecommendations.map((recommendation) => (
            <div
              key={recommendation.title}
              className={`smart-recommendation-card ${recommendation.type}`}
            >
              <div className="recommendation-card-top">
                <span className="recommendation-ai-badge">AI</span>
                <span className={`recommendation-priority ${recommendation.type}`}>
                  {recommendation.priority}
                </span>
              </div>

              <h4>{recommendation.title}</h4>
              <p>{recommendation.message}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="dashboard-section-grid dashboard-analytics-row">
        <div className="premium-panel analytics-panel dashboard-wide-panel">
          <div className="panel-title-row">
            <div>
              <span className="soft-badge">Expense Analytics</span>
              <h3>Expense Analytics</h3>
            </div>
            <p>{"\u20b9"}{format(totalExpense)} spent</p>
          </div>

          <div className="analytics-chart-grid">
            <div className="chart-glass-box">
              <div className="chart-title-row">
                <div>
                  <h3>Monthly Trend</h3>
                  <p>Month-wise expense movement</p>
                </div>
              </div>

              <div className="premium-chart-area">
                {expenses.length === 0 ? (
                  <p className="placeholder-text">No expense data available.</p>
                ) : (
                  <Line data={lineData} options={lineOptions} />
                )}
              </div>
            </div>

            <div className="chart-glass-box category-analytics-panel">
              <div className="chart-title-row">
                <div>
                  <h3>Spending Split</h3>
                  <p>Where your money is going</p>
                </div>
              </div>

              <div className="premium-chart-area pie-chart-area">
                {Object.keys(categoryTotals).length === 0 ? (
                  <p className="placeholder-text">No category data available.</p>
                ) : (
                  <Pie data={pieData} options={pieOptions} />
                )}
              </div>
            </div>
          </div>

          <div className="category-breakdown compact-category-breakdown">
            {topCategoryStats.length === 0 ? (
              <p className="placeholder-text">No category data available.</p>
            ) : (
              topCategoryStats.map(({ category, amount, percentage }, index) => (
                <div key={category} className="category-row">
                  <div className="category-row-main">
                    <span>{category}</span>
                    <strong>
                      {"\u20b9"}{Number(amount).toLocaleString("en-IN")} ({percentage}%)
                    </strong>
                  </div>

                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${percentage}%`,
                        background:
                          index === 0 ? "#22c55e" :
                          index === 1 ? "#38bdf8" :
                          "#a78bfa",
                      }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="dashboard-ai-stack">
          <div className="premium-panel ai-panel ask-ai-panel">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Ask AI</span>
                <h3>Ask AI About Your Spending</h3>
                <p className="ask-ai-subtitle">
                  Get intelligent financial insights from your monthly expense data.
                </p>
              </div>
            </div>

            <div className="ask-ai-input-row">
              <input
                type="text"
                placeholder="Ask about your expenses..."
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
              />

              <div className="ask-ai-button-group">
                <button type="button" className="glow-button" onClick={handleAskAI} disabled={askAiLoading}>
                  {askAiLoading ? "Thinking..." : "Ask"}
                </button>
                {aiAnswer && (
                  <button type="button" className="ai-reset-button" onClick={resetAskAI}>
                    Reset
                  </button>
                )}
              </div>
            </div>

            <div className="quick-prompt-row" aria-label="Quick AI prompts">
              {quickAskAIPrompts.map((prompt) => (
                <button
                  key={prompt.label}
                  type="button"
                  className="quick-prompt-pill"
                  onClick={() => setAiQuestion(prompt.question)}
                >
                  {prompt.label}
                </button>
              ))}
            </div>

            {demoMode && (
              <p className="ask-ai-demo-note">AI is analyzing sample financial data.</p>
            )}

            <div className={`ai-answer-card ${askAiLoading ? "loading" : ""} ${askAIInsight.state}`}>
              {askAiLoading ? (
                <div className="ai-thinking-row">
                  <span className="ai-loader"></span>
                  <p>AI is analyzing your expenses...</p>
                </div>
              ) : (
                <>
                  <div className="ai-answer-header">
                    <div>
                      <span>AI Insight</span>
                      <p>{renderInsightText(askAIInsight.summary)}</p>
                    </div>
                    <small className={`ai-status-badge ${askAIInsight.state}`}>
                      {askAIInsight.status}
                    </small>
                  </div>

                  {askAIInsight.highlights.length > 0 && (
                    <div className="ai-insight-section">
                      <div className="ai-section-heading">
                        <span>Key Highlights</span>
                      </div>
                      <ul className="ai-highlight-list">
                        {askAIInsight.highlights.map((highlight, index) => (
                          <li key={`${highlight}-${index}`}>
                            {renderInsightText(highlight)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {askAIInsight.recommendation && (
                    <div className="ai-recommendation-box">
                      <span className="ai-recommendation-icon" aria-hidden="true"></span>
                      <div>
                        <span>Recommendation</span>
                        <p>{renderInsightText(askAIInsight.recommendation)}</p>
                      </div>
                    </div>
                  )}

                  {askAIInsight.indicators.length > 0 && (
                    <div className="ai-spending-indicators">
                      {askAIInsight.indicators.map((indicator) => (
                        <div key={indicator.label}>
                          <span>{indicator.label}</span>
                          <strong>{renderInsightText(indicator.value)}</strong>
                        </div>
                      ))}
                      {askAISmartCards.length > 0 && (
                        <div>
                          <span>Confidence</span>
                          <strong>Structured</strong>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="premium-panel ai-panel insights-panel">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Gemini Insights</span>
                <h3>
                  AI Insights for {getMonthName(selectedMonth)} {selectedYear}
                </h3>
              </div>
            </div>

            {aiLoading ? (
              <div className="ai-loading-box">
                <span className="ai-loader"></span>
                <p>Generating smart insights...</p>
              </div>
            ) : (
              <>
                <div className="ai-metric-grid">
                  <div className="ai-metric-card">
                    <span>Total Spend</span>
                    <strong>{"\u20b9"}{formatMoney(aiTotalSpend)}</strong>
                  </div>

                  <div className="ai-metric-card">
                    <span>Top Category</span>
                    <strong>{aiTopCategory}</strong>
                  </div>

                  <div className="ai-metric-card">
                    <span>Top Spend</span>
                    <strong>{"\u20b9"}{formatMoney(aiTopAmount)}</strong>
                  </div>
                </div>

                <p className="ai-summary-text">
                  You have {aiExpenseCount} expenses totaling {"\u20b9"}{formatMoney(aiTotalSpend)}.
                  Your highest category is {aiTopCategory} with {"\u20b9"}{formatMoney(aiTopAmount)}.
                </p>

                <div className="ai-insight-box">
                  <h4>Smart Insight</h4>
                  <p>{smartInsight}</p>
                  <p>{aiInsightText}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-section-grid dashboard-planning-row">
        <div className="dashboard-planning-stack">
          <div className="premium-panel prediction-panel">
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Prediction</span>
                <h3>Next Month Forecast</h3>
              </div>
            </div>

            {prediction ? (
              <>
                <p className="prediction-main-amount">
                  {"\u20b9"}{format(prediction.predictedTotal)}
                </p>

                <div className="prediction-mini-grid">
                  <div>
                    <span>Average Monthly Spend</span>
                    <strong>{"\u20b9"}{format(prediction.averageMonthlySpend)}</strong>
                  </div>

                  <div>
                    <span>Top Risk Category</span>
                    <strong>{prediction.highestCategory}</strong>
                  </div>
                </div>

                <p className="prediction-message">{prediction.message}</p>
              </>
            ) : (
              <p>Prediction will appear after loading your expense data.</p>
            )}
          </div>

          <div className={`premium-panel comparison-panel ${monthComparisonStatus}`}>
            <div className="panel-title-row">
              <div>
                <span className="soft-badge">Monthly Comparison</span>
                <h3>
                  {getMonthName(comparison.selectedMonth || selectedMonth)} {comparison.selectedYear || selectedYear}
                  {" vs "}
                  {getMonthName(comparisonPreviousMonth)} {comparisonPreviousYear}
                </h3>
              </div>
            </div>

            <div className="comparison-mini-grid">
              <div>
                <span>Current Month Spend</span>
                <strong>{"\u20b9"}{format(comparison.currentTotal)}</strong>
              </div>

              <div>
                <span>Previous Month Spend</span>
                <strong>{"\u20b9"}{format(comparison.previousTotal)}</strong>
              </div>
            </div>

            <div className="comparison-change-row">
              <span className="comparison-icon">{comparisonIcon}</span>
              <div>
                <strong>
                  {comparisonDifferencePrefix}{"\u20b9"}{format(Math.abs(comparisonDifference))}
                </strong>
                <p>{comparisonPercentageText}</p>
              </div>
            </div>

            <p>{monthComparisonText}</p>
          </div>
        </div>

        <div className="premium-panel budget-alerts-panel">
          <div className="panel-title-row">
            <div>
              <span className="soft-badge">Budget Alerts</span>
              <h3>Risk Monitor</h3>
            </div>
            <p>{exceededBudgetCount} exceeded</p>
          </div>

          {visibleBudgetAlerts.length === 0 ? (
            <p className="placeholder-text">No budgets added yet.</p>
          ) : (
            visibleBudgetAlerts.map((alert) => (
              <div key={alert.category} className={`budget-progress-row ${alert.status}`}>
                <div>
                  <strong>{alert.category}</strong>
                  <p>
                    {"\u20b9"}{format(alert.spent)} spent of {"\u20b9"}{format(alert.budgetAmount)}
                  </p>
                </div>
                <span>{Math.round(alert.percentage)}%</span>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${Math.min(alert.percentage, 100)}%` }}
                  />
                </div>
              </div>
            ))
          )}

          {aiAlerts.length > 0 && (
            <div className="ai-alert-stack">
              {aiAlerts.slice(0, 3).map((alert, index) => (
                <div key={`${alert.title}-${index}`} className={`ai-alert ${alert.type}`}>
                  <strong>{alert.title}</strong>
                  <p>{alert.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-section-grid dashboard-history-row">
        <div className="premium-panel transaction-card">
          <div className="panel-title-row">
            <div>
              <span className="soft-badge">Recent Transactions</span>
              <h3>Latest Expenses</h3>
            </div>
          </div>

          {expenses.length === 0 ? (
            <p className="placeholder-text">No recent expenses available.</p>
          ) : (
            expenses.slice(0, 6).map((expense) => (
              <div key={expense._id || `${expense.category}-${expense.amount}-${expense.createdAt}`} className="recent-transaction-row">
                <div>
                  <strong>{expense.category || "Others"}</strong>
                  <p>{expense.text || expense.description || expense.title || "Expense"}</p>
                </div>
                <div>
                  <strong>{"\u20b9"}{format(expense.amount)}</strong>
                  <span>
                    {expense.createdAt
                      ? new Date(expense.createdAt).toLocaleDateString("en-IN")
                      : `${getMonthName(selectedMonth)} ${selectedYear}`}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="premium-panel category-forecast-card">
          <div className="panel-title-row">
            <div>
              <span className="soft-badge">Category Forecast</span>
              <h3>Expected Spending Split</h3>
            </div>
          </div>

          {prediction?.categoryPrediction ? (
            Object.entries(prediction.categoryPrediction)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([category, amount]) => {
                const maxAmount = Math.max(
                  ...Object.values(prediction.categoryPrediction)
                );

                const percentage = maxAmount > 0 ? (amount / maxAmount) * 100 : 0;

                const riskLabel =
                  percentage >= 70 ? "High Risk" : percentage >= 35 ? "Medium" : "Low";

                return (
                  <div key={category} className="forecast-risk-row">
                    <div className="forecast-risk-header">
                      <span>{category}</span>
                      <strong>{"\u20b9"}{format(amount)}</strong>
                    </div>

                    <div className="forecast-bar-bg">
                      <div
                        className={`forecast-bar ${
                          percentage >= 70
                            ? "risk-high"
                            : percentage >= 35
                            ? "risk-medium"
                            : "risk-low"
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>

                    <p className="forecast-risk-label">{riskLabel}</p>
                  </div>
                );
              })
          ) : (
            <p className="placeholder-text">Category forecast will appear after prediction data loads.</p>
          )}
        </div>
      </div>

    </div>
  );
}

export default Dashboard;

